import * as analysisState from '@/components/Analysis/state';
import { PageLoader } from '@/components/shared/PageLoader';
import { useErrorPage } from '@/hooks/useErrorPage';
import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { useFeaturesAccess } from '@/hooks/useFeaturesAccess';
import { useGetStructureFeaturesLazyQuery } from '@/__generated__/graphql';
import useAbility from '@/hooks/useAbility';

type Props = {
  structureId: string;
  feature: string;
  children: React.ReactNode;
};

export const AccessGuard = ({ structureId, feature, children }: Props) => {
  const [structureFeatures, setStructureFeatures] = useAtom(analysisState.structureFeatures);
  const [validity, setValidity] = useState<boolean>(false);
  const { setFeatureAccessFlags } = useFeaturesAccess();
  const redirectToErrorPage = useErrorPage(403);
  const { loading: abilitiesLoading } = useAbility();

  const [fetchFeatures, { loading }] = useGetStructureFeaturesLazyQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      if (!data?.structure?.features) {
        redirectToErrorPage?.();
        return;
      }

      const mainFeatures = Object.keys(data.structure.features);
      if (!mainFeatures.includes(feature)) {
        redirectToErrorPage?.();
        return;
      }

      const featuresWithAccess = setFeatureAccessFlags({ data });
      if (!featuresWithAccess) {
        redirectToErrorPage?.();
        return;
      }

      setStructureFeatures(featuresWithAccess);
      setValidity(true);
    },
  });

  useEffect(() => {
    if (!structureFeatures && !abilitiesLoading && !loading) {
      fetchFeatures();
    }
  }, [structureFeatures, abilitiesLoading, loading, fetchFeatures]);

  useEffect(() => {
    if (structureFeatures) {
      const mainFeatures = Object.keys(structureFeatures.features);
      if (!mainFeatures.includes(feature) && redirectToErrorPage) {
        redirectToErrorPage();
      } else {
        setValidity(true);
      }
    }
  }, [feature, redirectToErrorPage, structureFeatures]);

  return (
    <>
      {(loading || !structureFeatures || abilitiesLoading) && <PageLoader />}
      {!loading && !abilitiesLoading && validity && <>{children}</>}
    </>
  );
};
